import { useEffect, useState } from "react";
import { Shield, Lock, Users, Key, UsersRound } from "lucide-react";
import { PermissionsTab } from "./tabs/PermissionsTab";
import { RolesTab } from "./tabs/RolesTab";
import { RolePermissionsTab } from "./tabs/RolePermissionsTab";
import { MyAccessTab } from "./tabs/MyAccessTab";
import { GroupsTab } from "./tabs/GroupsTab";
import { Can } from "@/components/Can";
import { usePermissions } from "@/context/PermissionsProvider";

const TABS = [
  {
    id: "permissions",
    label: "Permissions",
    title: "Permissions",
    description: "Every scoped permission available to roles and access groups. Names follow the resource:action convention.",
    icon: Lock,
    permission: "permission:view",
  },
  {
    id: "roles",
    label: "Roles",
    title: "Roles",
    description: "Define platform roles and keep their descriptions up to date.",
    icon: Shield,
    permission: "role:view",
  },
  {
    id: "groups",
    label: "Groups",
    title: "Access groups",
    description: "Organize users into groups and attach roles for scoped access.",
    icon: UsersRound,
    permission: "role:view",
  },
  {
    id: "role-permissions",
    label: "Role → Perms",
    title: "Role permissions",
    description: "Map which permissions each role is allowed to use.",
    icon: Key,
    permission: "role:view",
  },
  {
    id: "my-access",
    label: "My Access",
    title: "My access",
    description: "Your assigned roles and the permissions granted to your account.",
    icon: Users,
    permission: null,
  },
] as const;

type TabId = (typeof TABS)[number]["id"];

export function AccessControlPage() {
  const { can } = usePermissions();
  const [activeTab, setActiveTab] = useState<TabId>("my-access");

  useEffect(() => {
    // If the initial tab is already accessible, keep it.
    const currentTabConfig = TABS.find((t) => t.id === activeTab);
    if (currentTabConfig && (!currentTabConfig.permission || can(currentTabConfig.permission))) {
      return;
    }

    // Fall back to the first available tab if current isn't permitted
    const firstPermitted = TABS.find((t) => !t.permission || can(t.permission));
    if (firstPermitted) {
      setActiveTab(firstPermitted.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const active = TABS.find((t) => t.id === activeTab && (!t.permission || can(t.permission)))
    ?? TABS.find((t) => !t.permission || can(t.permission))
    ?? TABS[0];

  return (
    <div className="flex h-full flex-col bg-slate-50">
      <div className="border-b border-slate-200/80 bg-white">
        <div className="px-6 pt-6 pb-4 sm:px-8">
          <div className="mb-1 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
            <Shield size={13} className="text-slate-400" />
            Access Control
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-[1.7rem]">
            {active.title}
          </h1>
          <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-slate-500">
            {active.description}
          </p>
        </div>

        <div className="flex gap-1 overflow-x-auto px-6 sm:px-8">
          {TABS.map((t) => {
            const visible = !t.permission || can(t.permission);
            if (!visible) return null;
            const isActive = activeTab === t.id;
            return (
              <button
                key={t.id}
                id={`access-tab-${t.id}`}
                onClick={() => setActiveTab(t.id)}
                className={[
                  "relative flex shrink-0 items-center gap-2 px-3.5 py-3 text-sm font-medium transition-colors",
                  isActive ? "text-slate-900" : "text-slate-500 hover:text-slate-800",
                ].join(" ")}
              >
                <t.icon size={15} className={isActive ? "text-bridge-accent" : ""} />
                {t.label}
                {isActive && (
                  <span className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-bridge-accent" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 sm:p-8">
        {activeTab === "permissions" && (
          <Can permission="permission:view">
            <PermissionsTab />
          </Can>
        )}
        {activeTab === "roles" && (
          <Can permission="role:view">
            <RolesTab />
          </Can>
        )}
        {activeTab === "groups" && (
          <Can permission="role:view">
            <GroupsTab />
          </Can>
        )}
        {activeTab === "role-permissions" && (
          <Can permission="role:view">
            <RolePermissionsTab />
          </Can>
        )}
        {activeTab === "my-access" && <MyAccessTab />}
      </div>
    </div>
  );
}
